import { Component, computed, inject, input } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { MatIconButton } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { MatSidenav, MatSidenavContainer, MatSidenavContent } from '@angular/material/sidenav';
import { NavigationEnd, Router, RouterLink, RouterLinkActive, RouterOutlet } from '@angular/router';
import { NotificationId, UserApplications, UserDto } from '@workspace/data-types';
import {
  ApplicationStore,
  NavigationItem,
  NotificationStateService,
  Scheme,
} from '@workspace/ng-data-access';
import { filter, map, startWith } from 'rxjs';
import { Media } from '../../../../../providers/media/media';
import { Theming } from '../../../../../providers/theming/theming';
import {
  AppsMenuButtonComponent,
  GlowingGradientButtonComponent,
  ProfileAppSwitcherComponent,
} from '../../../../buttons';
import { AuraImpersonateBannerComponent } from '../../../../layouts/banner/impersonate-banner/impersonate-banner.component';
import { SidebarUserAuraComponent } from '../../../../layouts/navigations/sidebar/user/sidebar-user.component';
import { AuraDiscussionsButtonComponent } from '../../../../notifications/discussions-button.component';
import { AuraNotificationsDropdownComponent } from '../../../../notifications/notifications-dropdown.component';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
  CollapsibleTriggerBehavior,
  Sidebar,
  SidebarBody,
  SidebarDivider,
  SidebarFooter,
  SidebarHeader,
  SidebarIcon,
  SidebarLabel,
  SidebarMenu,
  SidebarMenuItem,
  SidebarSection,
  SidebarSectionContent,
  SidebarSectionHeader,
} from '../../../ui';

@Component({
  selector: 'aura-sidebar-navigation-auxiliary-actions',
  imports: [
    MatIcon,
    MatIconButton,
    MatSidenav,
    MatSidenavContainer,
    MatSidenavContent,
    Sidebar,
    SidebarHeader,
    SidebarBody,
    SidebarFooter,
    SidebarSection,
    SidebarMenu,
    SidebarMenuItem,
    SidebarIcon,
    SidebarLabel,
    SidebarSectionContent,
    SidebarSectionHeader,
    SidebarDivider,
    Collapsible,
    CollapsibleContent,
    CollapsibleTrigger,
    CollapsibleTriggerBehavior,
    AuraImpersonateBannerComponent,
    SidebarUserAuraComponent,
    RouterLink,
    RouterLinkActive,
    RouterOutlet,
    AppsMenuButtonComponent,
    AuraNotificationsDropdownComponent,
    AuraDiscussionsButtonComponent,
    GlowingGradientButtonComponent,
    ProfileAppSwitcherComponent,
  ],
  host: { class: 'h-screen' },
  templateUrl: './auxiliary-actions.component.html',
})
export class SidebarNavigationAuxiliaryActions {
  private media = inject(Media);
  private readonly router = inject(Router);
  private readonly appStore = inject(ApplicationStore);
  protected theming = inject(Theming);
  protected readonly notificationState = inject(NotificationStateService);

  protected isMobile = computed(() => this.media.match(`(max-width: 767px)`)());
  user = input.required<UserDto | null>();
  navigationItems = input.required<NavigationItem[]>();
  appName = input<string>('ETXE');
  logoUrl = input<string>('/images/logo/logo.svg');
  appBadge = input<string | null>(null);
  homeUrl = input<string, string | undefined | null>('/', {
    transform: (v) => v || '/',
  });

  private readonly currentUrl = toSignal(
    this.router.events.pipe(
      filter((e): e is NavigationEnd => e instanceof NavigationEnd),
      map((e) => e.urlAfterRedirects.split('?')[0].split('#')[0]),
      startWith(this.router.url.split('?')[0].split('#')[0]),
    ),
    { initialValue: this.router.url.split('?')[0].split('#')[0] },
  );

  protected readonly isHomePage = computed(() => {
    const current = this.currentUrl();
    const target = this.homeUrl();
    if (target === '/') {
      return current === '/' || current === '' || current === '/home';
    }
    return current === target || current === '/' || current === '';
  });

  protected readonly notifications = this.notificationState.notifications;
  protected readonly unreadCount = this.notificationState.unreadCount;

  // expose scheme as a signal
  protected scheme = computed(() => this.theming.scheme());

  protected isAdminApp = computed(() => this.appStore.currentApplicationName() === UserApplications.ADMIN);
  protected isAgentApp = computed(() => this.appStore.currentApplicationName() === UserApplications.AGENT);
  protected hasProBadge = computed(() => !!this.appBadge() || this.isAgentApp());
  protected effectiveBadge = computed(() => this.appBadge() || (this.isAgentApp() ? 'Pro' : null));

  protected isSubscribed = computed(() => {
    const status = this.user()?.subscription?.status;
    return status === 'active' || status === 'trialing';
  });

  protected showExploreButton = computed(() => {
    const isAgentApp = this.appStore.currentApplicationName() === UserApplications.AGENT;
    const isAgent = !!this.user()?.profiles?.agent;
    return isAgentApp && isAgent;
  });

  protected planName = computed(() => {
    const plan = this.user()?.subscription?.plan as Record<string, unknown> | null | undefined;
    if (!plan) return null;
    if (typeof plan === 'object') {
      if (typeof plan['slug'] === 'string' && plan['slug']) {
        return plan['slug'].charAt(0).toUpperCase() + plan['slug'].slice(1);
      }
      if (typeof plan['name'] === 'string' && plan['name']) {
        return plan['name'];
      }
    }
    return 'Premium';
  });

  protected updateScheme(scheme: Scheme) {
    this.theming.scheme.update(() => scheme);
  }

  protected markAsRead(id: NotificationId) {
    this.notificationState.markAsRead(id);
  }

  protected markAllAsRead() {
    this.notificationState.markAllAsRead();
  }

  protected goToNotifications() {
    void this.router.navigate(['/user/notifications']);
  }
}
